<template>
  <Teleport to="body">
    <transition name="side-panel">
      <div v-if="open" class="side-panel-root" @click.self="$emit('close')">
        <aside class="side-panel" role="dialog" :aria-label="title">
          <header class="side-panel__header">
            <h2 class="side-panel__title">{{ title }}</h2>
            <button class="side-panel__close" @click="$emit('close')" aria-label="Close panel">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </header>
          <div class="side-panel__body">
            <slot />
          </div>
          <div v-if="$slots.footer" class="side-panel__footer">
            <slot name="footer" />
          </div>
        </aside>
      </div>
    </transition>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, watch } from 'vue'

export default defineComponent({
  name: 'SidePanel',
  props: {
    open: { type: Boolean, default: false },
    title: { type: String, default: '' },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && props.open) emit('close')
    }
    onMounted(() => window.addEventListener('keydown', onKey))
    onUnmounted(() => window.removeEventListener('keydown', onKey))
    watch(
      () => props.open,
      isOpen => {
        document.body.style.overflow = isOpen ? 'hidden' : ''
      }
    )
    return {}
  },
})
</script>

<style scoped>
.side-panel-root {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(10, 13, 25, 0.45);
  display: flex;
  justify-content: flex-end;
  font-family: var(--font-sans);
}

.side-panel {
  width: 420px;
  max-width: 100vw;
  height: 100vh;
  background: #1a2332;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  box-shadow: -12px 0 40px rgba(0, 0, 0, 0.4);
  box-sizing: border-box;
}

.side-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}

.side-panel__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.02em;
}

.side-panel__close {
  width: 32px;
  height: 32px;
  min-width: 32px;
  margin: 0;
  padding: 0;
  border-radius: 8px;
  background: transparent;
  border: none;
  color: var(--vt-c-accent-light, #b4fcda);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  transition: background 0.12s ease, color 0.12s ease;
}

.side-panel__close:hover {
  background: rgba(150, 255, 201, 0.12);
  color: var(--vt-c-accent, #96ffc9);
  transform: none;
  box-shadow: none;
}

.side-panel__body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px 24px;
}

.side-panel__footer {
  flex-shrink: 0;
  padding: 12px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

/* Slide + fade transition */
.side-panel-enter-active,
.side-panel-leave-active {
  transition: opacity 0.18s ease;
}
.side-panel-enter-active .side-panel,
.side-panel-leave-active .side-panel {
  transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}
.side-panel-enter-from,
.side-panel-leave-to {
  opacity: 0;
}
.side-panel-enter-from .side-panel,
.side-panel-leave-to .side-panel {
  transform: translateX(100%);
}
</style>
